import { Injectable,inject } from '@angular/core';
import { HttpService } from './http.service';
import { Observable, catchError, firstValueFrom, map, of, tap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
// import { environment } from '../../environments/environment';
// import { Project } from '../models/project.model';
import { LoginResponse } from '../models/user.model';
import { Router } from '@angular/router';
import { VarsDetails } from '../models/common.model';
import { AppEvent, KM } from '../stores/config.store';
import { ProjectEnv } from '../models/project.model';

// Credentials obtained for one eru instance (env); never the central eru token
export interface InstanceAuth {
  key: string;
  value: string;
  idTokenKey: string;
  idToken: string;
  userId: string;
}

export function instanceAuthHeaders(auth: InstanceAuth | null | undefined): Record<string, string> {
  const headers: Record<string, string> = {};
  if (!auth) return headers;
  if (auth.idToken) headers[auth.idTokenKey || 'id_token'] = auth.idToken;
  if (auth.value) headers[auth.key || 'Authorization'] = auth.value;
  return headers;
}
//import { EruAuthConfig } from '../models/eruauth.model';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private router = inject(Router);

  constructor(private httpService: HttpService) { }

   fetchConfig(url:string,project_name: string): Observable<{ project: any }> {
    
    return this.httpService.get<any>(
      `${url}/store/${project_name}/config`      
    ).pipe(
      map(response => {
        if (response && response.project) {
          return { project: response.project };
        } else {
          return { project: {} };
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Projects not found'));
      })
    );
  }

  refreshSecrets(url: string, project_name: string): Observable<any> {
    return this.httpService.get<any>(
        `${url}/store/${project_name}/sm/load`
    ).pipe(
        map(response => {
            if (response && response.msg) {
                return { message: response.msg };
            } else {
                return { message: 'No message received' };
            }
        }),
        catchError((error: HttpErrorResponse) => {
            console.error('API Error:', error);
            return throwError(() => new Error(error.error?.error || 'Failed to load secrets'));
        })
    )
}

    

    fetchKms(url: string, project_name: string): Observable<any> {
      
      return this.httpService.get<any>(
        `${url}/store/${project_name}/kms/list`
      )
        .pipe(
          map(response => {
            console.log('Raw KMS API response:', response);
            
            // If response is already in the expected format, return it
            if (response && response.kms) {
              return response;
            }
            
            // If response is the KMS object directly (no kms property)
            if (response && typeof response === 'object' && !Array.isArray(response)) {
              // Check if it's the KMS object structure with UUID keys
              const isKmsObject = Object.keys(response).some(key => 
                typeof response[key] === 'object' && 
                response[key].kms_name !== undefined
              );
              
              if (isKmsObject) {
                // Transform to array for consistent handling
                const kmsArray = Object.entries(response).map(([id, data]) => ({
                  id,
                  ...(data as any)
                }));
                
                return { kms: kmsArray };
              }
            }
            
            // Default case - empty array
            return { kms: [] };
          }),
          catchError((error: HttpErrorResponse) => {
            console.error('API Error:', error);
            return throwError(() => new Error(error.error?.error || 'KMS not found'));
          })
        );
    }

  fetchEvents(url:string,project_name: string): Observable<any> {
    return this.httpService.get<any>(
      `${url}/store/${project_name}/event/list`      
    ).pipe(
      map(response => {
        console.log('Raw Events API response:', response);
        
        // If response is already in the expected format, return it
        if (response && response.events) {
          return response;
        }
        
        // If response is the events object directly (no events property)
        if (response && typeof response === 'object' && !Array.isArray(response)) {
          // Check if it's the events object structure with UUID keys
          const isEventsObject = Object.keys(response).some(key => 
            typeof response[key] === 'object' && 
            response[key].event_name !== undefined
          );
          
          if (isEventsObject) {
            // Transform to array for consistent handling
            const eventsArray = Object.entries(response).map(([id, data]) => ({
              id,
              ...(data as any)
            }));
            
            return { events: eventsArray };
          }
        }
        
        // Default case - empty array
        return { events: [] };
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Events not found'));
      })
    )
  }

  

  // Logs into an instance and returns its auth without touching any store state.
  // responseKey is the login-response field holding the id token (env.auth_header_value).
  async loginForAuth(url: string, username: string, password: string, headerKey: string, responseKey: string): Promise<InstanceAuth> {
    if (!url) {
      throw new Error('Login URL is not configured for this environment');
    }
    const response: any = await firstValueFrom(this.loginWithUserPassword(url, username, password));
    const tokenField = responseKey || 'id_token';
    if (!response || !response[tokenField]) {
      throw new Error(`Login response has no "${tokenField}" field`);
    }
    return {
      key: headerKey || 'Authorization',
      value: response.access_token || '',
      idTokenKey: 'id_token',
      idToken: response[tokenField],
      userId: username || '',
    };
  }

  // Resolves the auth for an env from its stored security settings; null when it has none
  async loginEnv(env: Partial<ProjectEnv> | undefined | null): Promise<InstanceAuth | null> {
    if (!env) return null;
    if (env.auth_user_name?.trim() && env.auth_user_password?.trim()) {
      return this.loginForAuth(
        env.login_url_path || '',
        env.auth_user_name,
        env.auth_user_password,
        env.auth_header_name || '',
        env.auth_header_value || ''
      );
    }
    if (env.api_key_name?.trim() && env.api_key_value) {
      return { key: env.api_key_name, value: env.api_key_value, idTokenKey: 'id_token', idToken: '', userId: '' };
    }
    return null;
  }

  loginWithUserPassword(url: string, username: string, password: string): Observable<LoginResponse> {
    return this.httpService.post<LoginResponse>(
      url,
      { username, password }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Login failed'));
      })
    );
  }

  // Gateway settings are global (no project segment)
  saveProjectSettings(url: string, project_name: string | null, settings: Record<string, any>): Observable<any> {
    const path = project_name ? `${url}/store/${project_name}/settings/save` : `${url}/store/settings/save`;
    return this.httpService.post<any>(path, settings).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to save project settings'));
      })
    );
  }

  saveSecretManager(url: string, project_name: string, smType: string, secretManager: Record<string, any>): Observable<any> {
    return this.httpService.post<any>(
      `${url}/store/${project_name}/sm/save/${smType}`,
      secretManager
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to save secret manager'));
      })
    );
  }

  // eru-gateway exposes its config at /store/config without a project wrapper
  fetchGatewayConfig(url: string): Observable<any> {
    return this.httpService.get<any>(`${url}/store/config`).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Gateway config not found'));
      })
    );
  }

  saveVariables(url: string, project_name:string , variable: VarsDetails, varType : string): Observable<any> {
    return this.httpService.post<any>(
      `${url}/store/${project_name}/variables/save${varType}`,
      variable
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to save variables'));
      })
    );
  }

  saveKms(url: string, project_name:string , kms: KM): Observable<any> {
    return this.httpService.post<any>(
      `${url}/store/${project_name}/kms/save/${kms.kms_store_type}`,
      kms
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to save KMS'));
      })
    );
  }

  deleteKMS(url: string, project_name: string, id: string): Observable<any> {
    return this.httpService.delete<any>(
      `${url}/store/${project_name}/kms/remove/${id}`
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to delete KMS'));
      })
    );
  }

  saveEvents(url: string, project_name:string , event:AppEvent): Observable<any> {
    return this.httpService.post<any>(
      `${url}/store/${project_name}/event/save/${event.event_type}`,
      event
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to save events'));
      })
    );
  }

  deleteEvent(url: string, project_name: string, id: string): Observable<any> {
    return this.httpService.delete<any>(
      `${url}/store/${project_name}/event/remove/${id}`
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to delete events'));
      })
    );
  }
  
  deleteVariables(url: string, project_name:string , variable: VarsDetails, varType : string): Observable<any> {
    return this.httpService.delete<any>(
      `${url}/store/${project_name}/variables/remove${varType}/${variable.key}`,      
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('API Error:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to save variables'));
      })
    );
  }
}